import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import {
  TraceabilityResponse,
  TraceabilityDeepResponse,
  TraceabilitySearchParams,
  TraceabilitySearchOptions,
  WorkExecutionDetail,
} from './traceability.interfaces';

export interface TraceabilityConfig {
  apiUrl?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class TraceabilityService {
  private http = inject(HttpClient);
  private config: TraceabilityConfig | null = null;

  configure(config: TraceabilityConfig): void {
    this.config = config;
  }

  search(params: TraceabilitySearchParams): Observable<TraceabilityResponse> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workexecutions/trace/`;

    const queryParams: Record<string, string> = {
      identifier: params.identifier,
    };

    if (params.mode) {
      queryParams['mode'] = params.mode;
    }
    if (params.field) {
      queryParams['field'] = params.field;
    }
    if (params.dateFrom) {
      queryParams['date_from'] = params.dateFrom;
    }
    if (params.dateTo) {
      queryParams['date_to'] = params.dateTo;
    }
    if (params.status && params.status.length > 0) {
      queryParams['status'] = params.status.join(',');
    }
    if (params.page) {
      queryParams['page'] = String(params.page);
    }
    if (params.pageSize) {
      queryParams['page_size'] = String(params.pageSize);
    }

    return this.http.get<TraceabilityResponse>(url, { params: queryParams }).pipe(
      catchError((error) => {
        console.error('Error al buscar trazabilidad:', error);
        return throwError(() => error);
      }),
    );
  }

  searchDeep(params: TraceabilitySearchParams): Observable<TraceabilityDeepResponse> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workexecutions/trace/`;

    const queryParams: Record<string, string> = {
      identifier: params.identifier,
      mode: 'deep',
    };

    if (params.dateFrom) {
      queryParams['date_from'] = params.dateFrom;
    }
    if (params.dateTo) {
      queryParams['date_to'] = params.dateTo;
    }

    return this.http.get<TraceabilityDeepResponse>(url, { params: queryParams }).pipe(
      catchError((error) => {
        console.error('Error al buscar trazabilidad (modo profundo):', error);
        return throwError(() => error);
      }),
    );
  }

  getSearchOptions(): Observable<TraceabilitySearchOptions> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workexecutions/trace/search-options/`;

    return this.http.get<TraceabilitySearchOptions>(url).pipe(
      catchError((error) => {
        console.error('Error al obtener opciones de búsqueda:', error);
        return throwError(() => error);
      }),
    );
  }

  retryWorkflow(workflowId: number, executionListId: number): Observable<{ status: string; message: string }> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workflow/retry/${workflowId}/`;

    return this.http
      .post<{ status: string; message: string }>(url, {
        retry_source_execution_id: executionListId,
      })
      .pipe(
        catchError((error) => {
          console.error('Error al reintentar el workflow:', error);
          return throwError(() => error);
        }),
      );
  }

  getExecutionDetail(executionId: number): Observable<WorkExecutionDetail> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/workexecutions/${executionId}/`;

    return this.http.get<WorkExecutionDetail>(url).pipe(
      catchError((error) => {
        console.error('Error al obtener el detalle de la ejecución:', error);
        return throwError(() => error);
      }),
    );
  }

  private getApiUrl(): string {
    if (!this.config) {
      throw new Error('TraceabilityService no está configurado. Llama a configure() primero.');
    }
    return <string>this.config.apiUrl;
  }
}